<template>
    <v-card class="pa-5 margin-bottom">
        <h4>Filter</h4>
        <v-row class="mx-2 mt-3 justify-content-end">
            <v-col cols="3">
                <v-select
                    :items="dropdownItems.companies"
                    label="Company"
                    item-text="value"
                    item-value="id"
                    v-model.trim="filter.company"
                    multiple
                    @focusout="filterProbes"
                >
                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0">{{ item.value }} </span>
                        <span
                            v-if="index === 1"
                            class="grey--text text-caption"
                        > (+ {{ filter.company.length - 1 }} others)
        </span>
                    </template>
                </v-select>
            </v-col>
            <v-col cols="3">
                <v-select
                    :items="dropdownItems.regions"
                    label="Region"
                    v-model.trim="filter.region"
                    multiple
                    @focusout="filterProbes"
                >
                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0">{{ item }} </span>
                        <span
                            v-if="index === 1"
                            class="grey--text text-caption"
                        > (+ {{ filter.region.length - 1 }} others)
        </span>
                    </template>
                </v-select>
            </v-col>
            <v-col cols="3">
                <v-select
                    :items="dropdownItems.seasons"
                    label="Season"
                    item-text="title"
                    item-value="id"
                    v-model.trim="filter.season"
                    multiple
                    @focusout="filterProbes"
                    :disabled="filter.dateBetween.length > 0"
                >
                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0">{{ item.title }} </span>
                        <span
                            v-if="index === 1"
                            class="grey--text text-caption"
                        > (+ {{ filter.season.length - 1 }} others)
        </span>
                    </template>
                </v-select>
            </v-col>
            <v-col cols="3">
                <v-menu
                    class="date-to-menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            class="date-to-menu"
                            :value="dateRangeText(filter.dateBetween)"
                            prepend-icon="mdi-calendar"
                            v-bind="attrs"
                            v-on="on"
                            label="Date range"
                            :disabled="filter.season.length > 0"
                        ></v-text-field>
                    </template>
                    <v-date-picker
                        :show-current="false"
                        first-day-of-week="1"
                        range
                        v-model="filter.dateBetween"
                        @input="sendDates"
                    ></v-date-picker>
                </v-menu>
            </v-col>
        </v-row>
        <v-row class="mx-2 mt-0 pt-0">
            <v-col cols="4">
                <v-select
                    :items="dropdownItems.cultures"
                    label="Culture"
                    item-text="value"
                    item-value="culture_id"
                    v-model.trim="filter.culture"
                    multiple
                    @focusout="filterProbes"
                >
                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0">{{ item.value }} </span>
                        <span
                            v-if="index === 1"
                            class="grey--text text-caption"
                        > (+ {{ filter.culture.length - 1 }} others)
        </span>
                    </template>
                </v-select>
            </v-col>
            <v-col cols="4">
                <v-select
                    :items="dropdownItems.sensors"
                    label="Sensor depth"
                    v-model.trim="filter.sensor"
                    multiple
                    @focusout="filterProbes"
                >
                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0">{{ item }} </span>
                        <span
                            v-if="index === 1"
                            class="grey--text text-caption"
                        > (+ {{ filter.sensor.length - 1 }} others)
        </span>
                    </template>
                </v-select>
            </v-col>
            <v-col cols="4">
                <v-select
                    :items="analyticsDataProbeList"
                    item-text="name"
                    item-value="id"
                    v-model.trim="probeObjArray"
                    label="Probes"
                    multiple
                    return-object
                >
                    <template v-slot:selection="{ item, index }">
                        <span v-if="index === 0">{{ item.name }} </span>
                        <span
                            v-if="index === 1"
                            class="grey--text text-caption"
                        > (+ {{ probeObjArray.length - 1 }} others)
        </span>
                    </template>
                    <template v-slot:prepend-item>
                        <v-list-item
                            ripple
                            @mousedown.prevent
                            @click="handleSelectAllClick"
                        >
                            <v-list-item-action>
                                <v-icon color="indigo darken-4">
                                    {{ toggleAllIcon }}
                                </v-icon>
                            </v-list-item-action>
                            <v-list-item-content>
                                <v-list-item-title>
                                    Select All
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                    </template>
                </v-select>
            </v-col>
        </v-row>
        <v-row class="mx-2 mt-0 pt-0">
            <v-col cols="6" class="text-center mt-0 pt-0">
                <v-btn class="mt-4 generate-btn-margin" small color="success"
                       @click="filterProbeStationLocationsAndGenerateCsv">Generate
                </v-btn>
            </v-col>
            <v-col cols="6" class="text-center align-top mt-0 pt-0">
                <v-btn class="mt-4" small color="primary" @click="resetFilter">Reset</v-btn>
            </v-col>
        </v-row>
    </v-card>
</template>
<script>

import { mapActions, mapMutations, mapState } from 'vuex';
import moment from 'moment';

export default {
    name: 'DataAnalyticsFilterCard',
    data() {
        return {
            probeObjArray: [],
            filter: {
                company: [],
                region: [],
                season: [],
                culture: [],
                dateBetween: [],
                sensor: [],
            },
        };
    },
    computed: {
        ...mapState('analytics', ['analyticsDataProbeList', 'dropdownItems']),
        toggleAllIcon() {
            if (this.probeObjArray.length === this.analyticsDataProbeList.length) return 'mdi-close-box';
            if (this.probeObjArray.length > 0) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },


    },
    methods: {
        ...mapMutations('analytics', ['setAnalyticsFilter', 'setFilterItems']),
        ...mapActions('analytics', ['fetchDataAnalyticsList', 'generateCsvFile', 'getAnalyticsDataTableData']),
        dateRangeText(date) {
            let newDate = date.map(d => moment(d).format('YYYY-MM-DD'));
            return newDate.join(' - ');
        },
        async sendDates() {
            if (this.filter.dateBetween.length === 2) {
                this.filter.dateBetween = this.filter.dateBetween.map(d => moment(d).format('YYYY-MM-DD'));
                await this.filterProbes();
            }
        },
        async filterProbeStationLocationsAndGenerateCsv() {
            if (this.probeObjArray.length > 0) {
                let getProbeLocationIds = {};

                for (let probe in this.probeObjArray) {
                    let filterProbeLocations = this.probeObjArray[probe]?.analytics_locations;
                    if (this.filter.company.length > 0) {
                        filterProbeLocations = filterProbeLocations.filter(location => this.filter.company?.includes(location?.location_company_id));
                    }
                    if (this.filter.region.length > 0) {
                        filterProbeLocations = filterProbeLocations.filter(location => this.filter.region?.includes(location?.region));
                    }
                    if (this.filter.season.length > 0) {
                        filterProbeLocations = filterProbeLocations.filter(location => this.filter.season?.includes(location?.season_id));
                    }
                    if (this.filter.culture.length > 0) {
                        filterProbeLocations = filterProbeLocations.filter(location => this.filter.culture?.includes(location?.culture_id));
                    }

                    getProbeLocationIds[this.probeObjArray[probe]?.id] = filterProbeLocations.map(location => location?.id);
                }

                await this.generateCsvFile({probes: getProbeLocationIds, filters: this.filter, type: 'probe'});
                await this.getAnalyticsDataTableData();
                await this.resetFilter();
            }
        },
        async resetFilter() {
            this.filter.company = this.filter.region = this.filter.season = this.filter.dateBetween = this.filter.culture = this.filter.sensor = [];
            this.probeObjArray = [];
            await this.setAnalyticsFilter(this.filter);
        },
        async filterProbes() {
            await this.setAnalyticsFilter(this.filter);
        },
        handleSelectAllClick() {
            if (this.probeObjArray.length === this.analyticsDataProbeList.length) {
                this.probeObjArray = [];
            } else {
                this.probeObjArray = this.analyticsDataProbeList;
            }
        },
    },
    watch: {
        probeObjArray: {
            handler: async function() {
                await this.fetchDataAnalyticsList();
            },
        },
    },
};
</script>

<style>
.justify-content-end {
    justify-content: end;
}

.margin-bottom {
    margin-bottom: 10px;
}

.generate-btn-margin {
    margin: 0 15px;
}

::v-deep .input-width {
    max-width: 150px;
}
</style>